import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

export default class extends Controller {
  static targets = ["nameInput", "identifierInput", "resultsOutput", "dropdown"]

  ROR_API_URL = "https://api.ror.org/v2/organizations"
  DEBOUNCE_DELAY = 300 // milliseconds
  debounceTimer = null

  connect() {
    this.resetResults()
  }

  disconnect() {
    this.resetDebounceTimer()
  }

  resetDebounceTimer() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  handleInput(event) {
    event.preventDefault()
    event.stopPropagation()
    this.resetIdentifier()
    this.performSearch()
  }

  handleKeydown(event) {
    console.log("Keydown event:", event.key)

    if (event.key === "Escape") {
      this.closeDropdown()
      this.nameInputTarget.focus()
    } else if (event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      event.stopPropagation()
      this.openDropdown()
      this.focusFirstDropdownItem()
    }
  }

  performSearch() {
    const query = this.nameInputTarget.value.trim()

    if (query.length < 3) { // Minimum query length to trigger search
      this.resetResults()
      return
    }

    this.resetDebounceTimer()
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, this.DEBOUNCE_DELAY)
  }

  async fetchResults(query) {
    if (!query) {
      this.resetResults()
      return
    }

    try {
      const response = await fetch(`${this.ROR_API_URL}?query=${encodeURIComponent(query)}*`)
      if (!response.ok) {
        throw new Error(`ROR API request failed: ${response.statusText}`)
      }
      const data = await response.json()

      this.displayResults(data.items || [])
    } catch (error) {
      this.setTextResult("Error fetching ROR data", true)
      this.openDropdown()
      this.nameInputTarget.focus()
    }
  }

  _getDisplayName(item) {
    if (item.names && Array.isArray(item.names) && item.names.length > 0) {
      const preferredTypes = ["ror_display", "label"];

      for (const type of preferredTypes) {
        const nameObj = item.names.find(
          n => n && n.types && Array.isArray(n.types) && n.types.includes(type)
        );
        if (nameObj && typeof nameObj.value === 'string') {
          return nameObj.value;
        }
      }
    }

    return "N.a."; // Final fallback
  }

  displayResults(items) {
    this.resultsOutputTarget.innerHTML = ""

    if (items.length === 0) {
      this.setNoResult()
      return
    }

    items.forEach(item => {
      this.addResult(item)
    })

    this.openDropdown()
    this.nameInputTarget.focus()
  }

  setNoResult() {
    this.setTextResult("No results found")
  }

  setTextResult(message, error = false) {
    const resultElement = document.createElement("div")
    resultElement.classList.add("dropdown-item")
    resultElement.classList.add("fst-italic")
    resultElement.textContent = message

    if (error) {
      resultElement.classList.add("text-danger")
    }

    const listElement = document.createElement("li")
    listElement.appendChild(resultElement)

    this.resultsOutputTarget.innerHTML = ""
    this.resultsOutputTarget.appendChild(listElement)
  }

  addResult(item) {
    const displayName = this._getDisplayName(item);

    const resultElement = document.createElement("a")
    resultElement.href = "#"
    resultElement.classList.add("dropdown-item")
    resultElement.textContent = displayName

    resultElement.dataset.rorId = item.id
    resultElement.dataset.rorName = displayName
    resultElement.addEventListener("click", this.selectResult.bind(this))

    const listElement = document.createElement("li")
    listElement.appendChild(resultElement)

    this.resultsOutputTarget.appendChild(listElement)
  }

  selectResult(event) {
    event.preventDefault()
    event.stopPropagation()

    const selectedItem = event.currentTarget
    this.setName(selectedItem.dataset.rorName)
    this.setIdentifier(selectedItem.dataset.rorId)

    this.closeDropdown()
    this.nameInputTarget.focus()
  }

  resetResults() {
    this.setTextResult("Enter 3 or more characters to search organizations against ROR...")
  }

  openDropdown() {
    const dropdownInstance = Dropdown.getOrCreateInstance(this.dropdownTarget)
    if (dropdownInstance) {
      dropdownInstance.show()
    }
  }

  closeDropdown() {
    const dropdownInstance = Dropdown.getOrCreateInstance(this.dropdownTarget)
    if (dropdownInstance) {
      dropdownInstance.hide()
    }
  }

  setName(value) {
    if (this.hasNameInputTarget) {
      this.nameInputTarget.value = value
    }
  }

  setIdentifier(value) {
    if (this.hasIdentifierInputTarget) {
      this.identifierInputTarget.value = value
    }
  }

  resetIdentifier() {
    this.setIdentifier("")
  }

  focusFirstDropdownItem() {
    if (this.hasResultsOutputTarget) {
      const firstItem = this.resultsOutputTarget.querySelector('li:first-child > a.dropdown-item')
      if (firstItem) {
        firstItem.focus()
      }
    }
  }
}
